import { Card } from 'components/Card';
import { CardHintDisplay } from 'components/CardHintDisplay';
import { addToLevel } from 'game/core/Playable2DRootContainer';
import { HintItem, HintType } from 'game/gameHints/hintGroup';
import { Layout } from 'game/LayoutManager';
import { Container, Point, Sprite, Texture } from 'pixi.js';
import { getAsset } from 'game/preloadGameAssets';
import { shakePositions } from 'game/helpers/shakeAnimation';
import { MOVE_Z_INDEX } from 'constants/layout';
import { CARD_HINT_MOVE_TIME } from 'constants/move';
import gsap from 'gsap';
import { SolitaireGame } from 'game/SolitaireGame';
import { GameBoardPlace } from 'game/GameBoardPlace';

export class TutorialMoveGroup {
    glowScale = 420 / 363;
    glowTop = 32;
    borderTextureTop?: Texture;
    borderTextureMiddle?: Texture;
    borderTextureBottom?: Texture;
    borderTextureTopCut?: Texture;
    borderTextureBottomCut?: Texture;
    borderTextureMiddleCut?: Texture;
    game: SolitaireGame;
    tween: gsap.core.Tween[] = [];
    hintContainer: Container;
    targetPlace?: GameBoardPlace; // Default target place, can be changed based on the tutorial step
    constructor(game: SolitaireGame, moveGroup: string[], to: GameBoardPlace) {
        this.game = game;
        this.hintContainer = new Container();
        addToLevel(this.hintContainer);

        this.createBorderTexture().then(() => {
            this.createHintItem(moveGroup, to);
        });
    }

    createHintItem(moveGroup: string[], gamePlace: GameBoardPlace) {
        let moveType = HintType.MOVE;
        //constructor(moveGroup: Card[], to: GameBoardPlace, place: HintType, priority: number, sub = 0)

        const moveGroupCards = moveGroup.map((cardCode) =>
            this.game.cards.find((card) => card.code === cardCode)
        ) as Card[];

        const hintItem = new HintItem(
            moveGroupCards, //
            gamePlace,
            moveType,
            0
        );
        this.targetPlace = gamePlace;

        this.showHint([hintItem]);
    }
    showHint(hintItems: HintItem[]) {
        const hintDuration = CARD_HINT_MOVE_TIME;
        // SoundManager.play('hintSFX');

        hintItems.forEach((hintItem, groupIndex) => {
            let { moveGroup, to, type } = hintItem;
            let zIndex = MOVE_Z_INDEX - groupIndex;
            if (type === HintType.MOVE_STOCKPILE) {
                moveGroup = [moveGroup[0]];
            }
            if (type === HintType.RECYCLE) {
                moveGroup = [];
                zIndex = -1;
            }
            let moveTo = moveGroup.length
                ? (moveGroup[0].container.position as Point)
                : (to.container.position as Point);
            if (type === HintType.DEAL_SPIDER) {
                const lastStockPile = this.game.stockPile.lastCard;
                if (lastStockPile) {
                    moveTo = to.getNextCardPosition();
                }
            }

            this.createHintGroup(moveGroup, type);
            if (!this.hintContainer.position) return; // Ensure position is set before using it
            this.hintContainer.position.set(moveTo.x, moveTo.y); //do not use position = moveTo (not always observable point)
            this.hintContainer.zIndex = zIndex;
            const targetPosition = to.getNextCardPosition();

            if (type !== HintType.DEAL_SPIDER) {
                this.tween.push(
                    gsap.to(this.hintContainer, {
                        x: targetPosition.x,
                        y: targetPosition.y + Layout.getCardGap().normal * groupIndex,
                        duration: hintDuration,
                        ease: 'power1.inOut',
                        delay: groupIndex * hintDuration,
                        onComplete: () => {
                            this.hintContainer.zIndex = zIndex - 2;
                        },
                    })
                );
            } else {
                this.shakeStockPile();
            }
            this.tween.push(
                gsap.to(this.hintContainer, {
                    alpha: 0,
                    duration: hintDuration / 1.75,
                    delay: hintItems.length * hintDuration,
                    onComplete: () => {
                        this.hintContainer.destroy();
                    },
                })
            );
        });
    }
    shakeStockPile() {
        const { x, y } = this.hintContainer.position;
        shakePositions.forEach((position, index) => {
            gsap.to(this.hintContainer, {
                x: x + position.x * 2,
                y: y + position.y * 2,
                duration: 0.1,
                delay: index * 0.1,
            });
        });
    }
    async createBorderTexture() {
        this.borderTextureTop = await getAsset(`hints/glowTop`);
        this.borderTextureBottom = await getAsset(`hints/glowBottom`);
        this.borderTextureMiddle = await getAsset(`hints/glowMiddle`);

        this.borderTextureTopCut = await getAsset(`hints/glowTopCut`);
        this.borderTextureBottomCut = await getAsset(`hints/glowBottomCut`);
        this.borderTextureMiddleCut = await getAsset(`hints/glowMiddleCut`);
    }
    createHintGroup(moveGroup: Card[], type: HintType): void {
        const { width, height } = Layout.getCardSize();
        let groupHeight = 0;
        const startPosition = moveGroup.length ? moveGroup[0].position : new Point(0, 0);
        const glowContainer = new Container();
        this.hintContainer.addChild(glowContainer);
        moveGroup.forEach((card) => {
            const hintCard = new CardHintDisplay(card);
            groupHeight = Math.abs(startPosition.y - card.position.y);
            hintCard.container.position.y = card.position.y - startPosition.y;
            this.hintContainer.addChild(hintCard.container);
        });
        const glowWidth = width * this.glowScale;
        const topSprite = new Sprite(type === HintType.RECYCLE ? this.borderTextureTopCut : this.borderTextureTop);
        glowContainer.addChild(topSprite);
        topSprite.anchor.set(0.5, 0);
        topSprite.width = glowWidth;
        topSprite.position.set(0, -height / 2 - this.glowTop * topSprite.scale.x);
        topSprite.scale.y = topSprite.scale.x;

        const bottomSprite = new Sprite(
            type === HintType.RECYCLE ? this.borderTextureBottomCut : this.borderTextureBottom
        );
        glowContainer.addChild(bottomSprite);
        bottomSprite.anchor.set(0.5, 1);
        bottomSprite.width = glowWidth;
        bottomSprite.position.set(0, height / 2 + this.glowTop * bottomSprite.scale.x + groupHeight);
        bottomSprite.scale.y = bottomSprite.scale.x;

        const middleSprite = new Sprite(
            type === HintType.RECYCLE ? this.borderTextureMiddleCut : this.borderTextureMiddle
        );
        glowContainer.addChild(middleSprite);
        middleSprite.anchor.set(0.5);
        middleSprite.width = glowWidth;
        middleSprite.position.set(0, groupHeight / 2);
        middleSprite.height = groupHeight + height - height * 0.26;
    }
    destroy() {
        this.tween.forEach((t) => t.kill());
        this.tween = [];

        this.hintContainer.removeChildren();
        this.hintContainer.destroy();
    }
}
